import React, { useEffect, useRef, useState } from 'react';
import axios from "axios";
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { TbSortAscending2Filled } from "react-icons/tb";

import { BsFiletypeXlsx } from "react-icons/bs";
import { Tooltip } from 'react-tooltip';

import webLinkImg from '../../images/link.svg';
import instagramImg from '../../images/instagram.svg';
import facebookImg from '../../images/facebook.svg';
import linkedinImg from '../../images/linkedin.svg';
import youtubeImg from '../../images/youtube.svg';
import twitterImg from '../../images/twitter.svg';

import AddRecommendBy from './addRecommendByPopup';
import EditRecommendedBy from './editRecommendByPopup';
import Spinner from '../../components/spinner';
import { useLanguage } from '../../context/languageContext';
import GenerateCSV from '../../components/popup/generateCSV';
import FilterBar from '../../components/filterBar';
import SortBy from '../../components/sortBy';
import { useFilterChips } from '../../components/filterBar/useFilterChips';
import AddedBy from '../../components/addedBy';
import Pagination from '../../components/pagination';
import NoContent from '../../components/notFound/noBooks';
import { formatMongoDBTime } from '../../utills/dateAndTime';
import { convertToCloudFrontUrl, fetchAllGenresByLangOptions, fetchAllProfessionOptions , fetchAllActorNames } from '../../helper';
import { CFormSwitch } from '@coreui/react';
import { Can } from "../../context/permissionContext";
import { joinList } from "../../helper/listSeparator";


function Reference() {
  const { adminInfo, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
  const apiParamsRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [selectedRefIds, setSelectedRefIds] = useState([]);
  const [selectAllRefs, setSelectAllRefs] = useState(false);
  const [recommendedData, setRecommendedData] = useState(null);
  const [categoryId, setCategoryId] = useState([]);

  const [isAddRecommendedByModalVisible, setIsAddRecommendedByModalVisible] = useState(false);
  const [isEditRecommendedByModalVisible, setIsEditRecommendedByModalVisible] = useState(false);


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalNumOfRecommenders, setTotalNumOfRecommenders] = useState(0);

  // Search state

  // Sorting state
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  // The Sort by control. Sent on every request, so it is the list's order
  // of record; the column arrow below keeps itself in step with it.
  const [sortBy, setSortBy] = useState('latestCreated');

  const totalPages = Math.ceil(totalNumOfRecommenders / itemsPerPage);


  // The chip row under the search box. `chipFilters` is already in the

  // shape the endpoint reads, so it merges straight into `apiFilters`.

  const filters = useFilterChips({ supportsMatchAny: true, stickyField: 'name' });

  const apiFilters = {
    currentPage,
    itemsPerPage,
    langCode: "en",
    sortOrder: sortOrder,
    sortBy,
    // LAST on purpose: the single-field search above writes '' into every
    // field it is not searching, so a spread placed above it is wiped.
    ...filters.chipFilters,
    matchMode: filters.matchMode,
  };

  // Profession and genre are managed lists, so their filters offer the stored
  // names instead of asking anyone to spell them. Fetched once and shared by
  // the search box and the matching chips.
  const [professionNames, setProfessionNames] = useState([]);
  const [genreNames, setGenreNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    Promise.all([
      fetchAllProfessionOptions(adminInfo.token, 'en'),
      fetchAllGenresByLangOptions('en', adminInfo.token),
    ]).then(([professions, genres]) => {
      if (cancelled) return;
      setProfessionNames(professions.map((p) => p.label));
      setGenreNames(genres.map((g) => g.genreName));
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);


  // The names behind Created by / Edited by, so those filters are picked from
  // the team rather than spelled from memory.
  const [actorNames, setActorNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllActorNames(adminInfo.token).then((names) => {
      if (!cancelled) setActorNames(names);
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  const filterOptions = [
    { value: 'name', label: 'Name' },
    { value: 'profession', label: 'Profession', suggestions: professionNames },
    { value: 'recommendedBooks', label: 'Books Recommended' },
    { value: 'recommendedGenres', label: 'Recommended Genres', suggestions: genreNames },
    { value: 'booksWritten', label: 'Books Written' },
    { value: 'createdAt', label: 'Created on' },
    { value: 'updatedAt', label: 'Updated on' },
    { value: 'createdBy', label: 'Created by', suggestions: actorNames },
    { value: 'updatedBy', label: 'Edited by', suggestions: actorNames }
  ];

  const suggestionsByField = {
    profession: professionNames,
    recommendedGenres: genreNames,
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, sortOrder, sortBy, filters.signature]);

  useEffect(() => {
    if (!adminInfo?.token) {
      return;
    }

    document.title = "Penspace - Recommenders";
    const keyValues = JSON.stringify({ currentPage, itemsPerPage, sortOrder, sortBy, filters: filters.signature });

    if (apiParamsRef.current === keyValues) {
      return;
    }

    apiParamsRef.current = keyValues;
    fetchAllRecommenedersData();
  }, [adminInfo, currentPage, itemsPerPage, sortOrder, sortBy, filters.signature]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };


  const handleSort = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
      // Keeps the arrow live now that Sort by decides the order.
      setSortBy(prev => (prev === 'oldestCreated' ? 'latestCreated' : 'oldestCreated'));
  };

  const handleSelectRefId = (seletedRowId) => {
    if (selectedRefIds.includes(seletedRowId)) {
      setSelectedRefIds(prevState => prevState.filter(productId => productId !== seletedRowId));
    } else {
      setSelectedRefIds(prevState => [...prevState, seletedRowId]);
    }
  };

  const handleSelectAllRefs = () => {
    if (selectAllRefs) {
      setSelectedRefIds([]);
    } else {
      setSelectedRefIds(recommendedData?.map((label) => label._id));
    }
    setSelectAllRefs((prevSelectAll) => !prevSelectAll);
  };

  const fetchAllRecommenedersData = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/getall`, { filters: apiFilters }, { headers });

      if (response.status) {
        const recommendedData = response?.data || [];
        // Normalize recommend images: convert S3 URLs to CloudFront URLs immediately after API fetch
        const normalizedRecommenders = recommendedData.recommendersDocs?.map(recommend => {
          if (!recommend || !recommend.image) {
            return recommend;
          }
          
          const normalized = { ...recommend };
          if (normalized.image?.uploadedImageUrl) {
            normalized.image = {
              ...normalized.image,
              uploadedImageUrl: convertToCloudFrontUrl(normalized.image.uploadedImageUrl)
            };
          }
          
          return normalized;
        }) || [];
        
        setRecommendedData(normalizedRecommenders);
        setTotalNumOfRecommenders(recommendedData.totalCount || 0);
      }
    } catch (error) {
      console.error("fetchAllRecommenedersData catch error::", error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const handleDeleteRows = async () => {
    try {
      const headers = {
        Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null,
      };
      const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/delete`, { selectedRefIds }, { headers });

      if (response.status) {
        setSelectedRefIds([]);
        setSelectAllRefs(false);
        fetchAllRecommenedersData();
        toast.success(response.message || "Recommenders moved to the trash.");
      }
    } catch (error) {
      console.error("handleDeleteRows catch error::", error);
    }
  };

  const handleChangeRecommenderStatus = async (recommenderId, recommenderName, isActive) => {
    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/update/status/${recommenderId}`, { isActivatedOnHomePage: !isActive }, { headers });

      if (response.status) {
        toast.success(!isActive ? `${recommenderName}'s profile has been activated.` : `${recommenderName}'s profile has been deactivated.`);
        setRecommendedData((prevValues) =>
          prevValues?.map((item) =>
            item._id === recommenderId
              ? { ...item, isActivatedOnHomePage: !isActive }
              : item
          )
        );
      }
    } catch (error) {
      console.error("handleChangeRecommenderStatus catch error::", error);
    }
  };

  const handleAddRecommendBy = () => {
    setIsAddRecommendedByModalVisible(true);
  };

  const handleEditRecommendBy = (catId) => {
    setCategoryId(catId);
    setIsEditRecommendedByModalVisible(true);
  };

  const fetchNewRecommendByData = () => {
    setIsAddRecommendedByModalVisible(false);
    setIsEditRecommendedByModalVisible(false);
    fetchAllRecommenedersData();
  };

  const handleCloseAddRecommendedByModalOpen = () => {
    setIsAddRecommendedByModalVisible(false);
  };

  const handleCloseEditRecommendedByModalOpen = () => {
    setIsEditRecommendedByModalVisible(false);
  };




  return (
    <React.Fragment>
      <div className="heading">
        <h4>{totalNumOfRecommenders > 0 ? `Recommenders (${totalNumOfRecommenders})` : `Recommenders`}</h4>
        <div className="d-flex gap-2">
          {selectedRefIds.length > 0 ? (
            <Can feature="recommenders" action="delete"><button type="button" className="btn btn-outline-danger" onClick={handleDeleteRows}>Delete</button></Can>
          ) : (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>Download Excel <BsFiletypeXlsx /></button>
              <Can feature="recommenders" action="create"><button type="button" className="btn btn-dark" onClick={handleAddRecommendBy}>Add Recommender</button></Can>
            </div>
          )}
        </div>
      </div>
      <div className='d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center'>
        <SortBy value={sortBy} onChange={setSortBy} />
        {totalPages > 1 && (
          <div className='d-flex justify-content-center justify-content-lg-end w-100 w-lg-auto ms-lg-auto'>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalNumOfRecommenders}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showTotalItems={true}
            />
          </div>
        )}
      </div>
      <FilterBar
        supportsMatchAny
      options={filterOptions}
      chips={filters.chips}
      matchMode={filters.matchMode}
      onMatchModeChange={filters.setMatchMode}
      onAdd={filters.addChip}
      onUpdate={filters.updateChip}
      onRemove={filters.removeChip}
      onClear={filters.clearChips}
      />
      {loading ? (
        <Spinner />
      ) : (
        <div className="table_scrollbar table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    className="box form-check-input"
                    type="checkbox"
                    checked={selectAllRefs}
                    onChange={handleSelectAllRefs}
                  />
                </th>
                <th>S.no</th>
                <th>Image</th>
                  <th>Name
                    <TbSortAscending2Filled 
                      size={18}
                      style={{ cursor: 'pointer', marginLeft: '3px' }}
                      onClick={handleSort}
                      title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    />
                  </th>
                <th>Profession</th>
                <th>About</th>
                <th>Books Recommended</th>
                <th>Recommended Genres</th>
                <th>Created on</th>
                <th>Updated on</th>
                <th>Added by</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {recommendedData?.length > 0 ? (
                recommendedData.map((recommend, index) => {
                  const isSelectedValues = selectedRefIds.some((selectedId) => selectedId === recommend._id);
                  const showSelectedLanguage = recommend.translations;
                  const globalIndex = (currentPage - 1) * itemsPerPage + index;

                  return (
                    recommend && (
                      <tr key={recommend._id}>
                        <td className="checkBox">
                          <input
                            className={`box form-check-input`}
                            type="checkbox"
                            checked={isSelectedValues}
                            onChange={() => handleSelectRefId(recommend._id)}
                          />
                        </td>
                        <td className="index text-center">{globalIndex + 1}</td>
                        <td className="image">
                          {recommend.image?.uploadedImageUrl ? (
                            <img src={convertToCloudFrontUrl(recommend.image.uploadedImageUrl)} alt="" size={24} />
                          ) : (
                            <p className="image-skeleton" aria-hidden="true"><span>NO COVER</span></p>
                          )}
                        </td>
                        <td className="max_width">{showSelectedLanguage.name}</td>
                        <td className="max_width">
                          {showSelectedLanguage.profession.map((author, professionIndex) => (
                            <li className="list_styled" key={`${recommend._id}-profession-${author.professionId}-${professionIndex}`}>{author.professionName}</li>
                          ))}
                        </td>
                        <td className="tooltip_content">
                          <span
                            data-tooltip-id={`tooltip-${index}`}
                            data-tooltip-content={showSelectedLanguage.about}
                            data-tooltip-place="top"
                          >
                            {showSelectedLanguage.about}
                          </span>
                          <Tooltip
                            id={`tooltip-${index}`}
                            className="custom-tooltip"
                            delayShow={200}
                            offset={10}
                            float={true}
                          />
                        </td>
                        <td className="max_width">
                          {showSelectedLanguage.recommendedBooks.map((book, bookIndex) => (
                            <li className="list_styled" key={`${recommend._id}-recommendedBook-${book.bookId || book.seriesId}-${bookIndex}`}>
                              {book.seriesId ? book.seriesName : book.bookName}
                              {book.seriesId && <span style={{ color: "var(--ps-text-muted)" }}> – series</span>}
                              {book.authorNames?.length > 0 && (
                                <span style={{ color: "var(--ps-text-muted)" }}> – {joinList(book.authorNames)}</span>
                              )}
                            </li>
                          ))}
                        </td>
                        <td className="max_width">
                          {showSelectedLanguage.recommendedGenres.map((genre, genreIndex) => (
                            <li className="list_styled" key={`${recommend._id}-recommendedGenre-${genre.categoryId}-${genreIndex}`}>{genre.genreName}</li>
                          ))}
                        </td>
                        <td className="min_width">
                          {formatMongoDBTime(recommend.createdAt)
                          }
                        </td>
                        <td className="min_width">{recommend.updatedAt ? formatMongoDBTime(recommend.updatedAt) : "—"}</td>
                        <td><AddedBy record={recommend} /></td>
                        <td className="min_width">
                          <CFormSwitch
                            size={`lg`}
                            variant={"3d"}
                            color={"primary"}
                            style={{ paddingLeft: 0, width: "42px", height: "22px", cursor: "pointer" }}
                            checked={recommend.isActivatedOnHomePage === true ? true : false}
                            onChange={() => handleChangeRecommenderStatus(recommend._id, showSelectedLanguage?.name, recommend.isActivatedOnHomePage)}
                          />
                        </td>
                        <td className="action">
                          <div onClick={() => handleEditRecommendBy(recommend._id)}><FaEdit size={20} /></div>
                        </td>
                      </tr>
                    )
                  );
                })
              ) : (
                <NoContent title="Recommenders" colSpan={13} />
              )}
            </tbody>
          </table>
        </div>
      )}

      <AddRecommendBy
        isAddRecommendedByModalVisible={isAddRecommendedByModalVisible}
        setIsAddRecommendedByModalVisible={setIsAddRecommendedByModalVisible}
        closeModal={handleCloseAddRecommendedByModalOpen}
        onSuccess={fetchNewRecommendByData}
      />

      <EditRecommendedBy
        categoryId={categoryId}
        isModalVisible={isEditRecommendedByModalVisible}
        closeModal={handleCloseEditRecommendedByModalOpen}
        onSuccess={fetchNewRecommendByData}
      />

      <GenerateCSV
        title="recommenders"
        isShowCSVModalVisible={isShowCSVModalVisible}
        handleCloseCSV={handleCloseCSV}
        handleGenerateCSV={() => handleGenerateCSV("recommenders", apiFilters, "Excel")}
        // The server sends a workbook (recommenders.xlsx), not a CSV.
        excel
        excelTabs={["Recommended Books", "Books Written"]}
      />
    </React.Fragment>
  );
}

export default Reference
